import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useParams, useNavigate } from "react-router-dom";
import { toast } from "react-hot-toast";
import {
  PencilIcon,
  TrashIcon,
  ExclamationTriangleIcon,
  CheckCircleIcon,
  ClockIcon,
  XCircleIcon,
  BeakerIcon,
  DocumentIcon,
  ChatBubbleLeftRightIcon,
  ClockIcon as HistoryIcon,
  DocumentPlusIcon,
  CheckBadgeIcon,
  PrinterIcon,
} from "@heroicons/react/24/outline";

import { chemicalInventoryApi } from "../../../../../api/chemical-inventory";
import { ResourceLoading } from "../../../../../components/utils/ResourceLoading";
import { ResourceError } from "../../../../../components/utils/ResourceError";
import { errorStateVariantFromError } from "../../../../../components/utils/errorStateUtils";
import { DocumentManagerV6 } from "../../../../../components/dashboard/Media/DocumentManagerV6";
import { NotesDeluxeSection } from "../../../../../components/dashboard/notes/notesDeluxeSection";
import { ChemicalInventoryForm } from "../../../../../components/dashboard/quality/chemical-inventory-form";
import { ChemicalInventoryReviewBadge } from "../../../../../components/dashboard/quality/chemical-inventory-review-badge";
import { HazardSymbolsDisplay } from "../../../../../components/dashboard/quality/hazard-symbols-selector";
import { HsePageChrome, useHseBusyAction } from "../../../../../components/dashboard/hse/HsePageChrome";
import { PMDialog } from "../../../../../components/utils/PMDialog";
import { useUserHasAccess } from "../../../../../routes/roleOnly";
import { CHEMICAL_INVENTORY_WRITE_ROLES } from "../../../../../utils/chemicalInventoryAccess";
import { RiskSubnav } from "../../../../../components/dashboard/hse/risk/RiskSubnav";

export const HseKsChemicalInventoryView = () => {
  const { t } = useTranslation();
  const { id } = useParams();
  const navigate = useNavigate();
  const canWrite = useUserHasAccess({ roles: CHEMICAL_INVENTORY_WRITE_ROLES });
  const [chemical, setChemical] = useState(null);
  const [revisions, setRevisions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [errorVariant, setErrorVariant] = useState("load_error");
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [showPublishDialog, setShowPublishDialog] = useState(false);
  const [changeSummary, setChangeSummary] = useState("");
  const [nextReviewDate, setNextReviewDate] = useState("");
  const [publishing, setPublishing] = useState(false);
  const [creatingRevision, setCreatingRevision] = useState(false);
  const { run, busyProps } = useHseBusyAction();

  const fetchChemical = async () => {
    try {
      setLoading(true);
      const response = await chemicalInventoryApi.getChemicalInventory(id);
      setChemical(response.data || response);
    } catch (err) {
      console.error("Error fetching chemical inventory:", err);
      const variant = errorStateVariantFromError(err);
      setError(true);
      setErrorVariant(variant);
      if (variant === "load_error") {
        toast.error(t("chemical.inventory.load_error"));
      }
    } finally {
      setLoading(false);
    }
  };

  const fetchRevisions = async () => {
    try {
      const response = await chemicalInventoryApi.getRevisions(id);
      const list = response?.data || response?.revisions || response || [];
      setRevisions(Array.isArray(list) ? list : []);
    } catch {
      setRevisions([]);
    }
  };

  useEffect(() => {
    fetchChemical();
    fetchRevisions();
  }, [id]);

  const handleDelete = async () => {
    if (!window.confirm(t("chemical.inventory.delete_confirm"))) return;
    try {
      await chemicalInventoryApi.deleteChemicalInventory(id);
      toast.success(t("chemical.inventory.deleted"));
      navigate("/dashboard/hse-ks/risk/chemical-inventory");
    } catch (err) {
      toast.error(t("chemical.inventory.delete_error"));
    }
  };

  const handleNewRevision = async () => {
    setCreatingRevision(true);
    try {
      await chemicalInventoryApi.createRevision(id);
      toast.success(t("chemical.inventory.revision_created"));
      await fetchChemical();
      await fetchRevisions();
    } catch {
      toast.error(t("chemical.inventory.revision_create_error"));
    } finally {
      setCreatingRevision(false);
    }
  };

  const handlePublishReview = async () => {
    if (!String(changeSummary || "").trim()) {
      toast.error(t("chemical.inventory.change_summary_required"));
      return;
    }
    setPublishing(true);
    try {
      await chemicalInventoryApi.publishReview(id, {
        change_summary: changeSummary.trim(),
        next_review_date: nextReviewDate || undefined,
      });
      toast.success(t("chemical.inventory.review_published"));
      setShowPublishDialog(false);
      setChangeSummary("");
      setNextReviewDate("");
      await fetchChemical();
      await fetchRevisions();
    } catch {
      toast.error(t("chemical.inventory.review_publish_error"));
    } finally {
      setPublishing(false);
    }
  };

  const getHmsLevelIcon = (level) => {
    switch (level) {
      case 1:
        return <CheckCircleIcon className="h-5 w-5 text-green-500" />;
      case 2:
        return <ClockIcon className="h-5 w-5 text-yellow-500" />;
      case 3:
        return <ExclamationTriangleIcon className="h-5 w-5 text-orange-500" />;
      case 4:
        return <XCircleIcon className="h-5 w-5 text-red-500" />;
      default:
        return <BeakerIcon className="h-5 w-5 text-gray-500" />;
    }
  };

  const getStatusBadge = (status) => {
    const baseClasses =
      "inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium";
    switch (status) {
      case "active":
        return (
          <span className={`${baseClasses} bg-green-100 text-green-800`}>
            {t("chemical.inventory.status.active")}
          </span>
        );
      case "inactive":
        return (
          <span className={`${baseClasses} bg-gray-100 text-gray-800`}>
            {t("chemical.inventory.status.inactive")}
          </span>
        );
      case "archived":
        return (
          <span className={`${baseClasses} bg-yellow-100 text-yellow-800`}>
            {t("chemical.inventory.status.archived")}
          </span>
        );
      default:
        return (
          <span className={`${baseClasses} bg-gray-100 text-gray-800`}>
            {status}
          </span>
        );
    }
  };

  const getHmsLevelBadge = (level) => {
    const baseClasses =
      "inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium";
    switch (level) {
      case 1:
        return (
          <span className={`${baseClasses} bg-green-100 text-green-800`}>
            {t("chemical.inventory.hms_level.1")}
          </span>
        );
      case 2:
        return (
          <span className={`${baseClasses} bg-yellow-100 text-yellow-800`}>
            {t("chemical.inventory.hms_level.2")}
          </span>
        );
      case 3:
        return (
          <span className={`${baseClasses} bg-orange-100 text-orange-800`}>
            {t("chemical.inventory.hms_level.3")}
          </span>
        );
      case 4:
        return (
          <span className={`${baseClasses} bg-red-100 text-red-800`}>
            {t("chemical.inventory.hms_level.4")}
          </span>
        );
      default:
        return null;
    }
  };

  if (loading) {
    return <ResourceLoading size="base" />;
  }

  if (error || !chemical) {
    return (
      <ResourceError
        variant={!chemical && !error ? "not_found" : errorVariant}
        errors={
          errorVariant === "load_error" && error
            ? [t("chemical.inventory.load_error")]
            : undefined
        }
        onRetry={error ? fetchChemical : undefined}
        backTo="/dashboard/hse-ks/risk/chemical-inventory"
      />
    );
  }

  const historyItems =
    revisions.length > 0
      ? revisions
      : chemical.chemical_inventory_histories || [];

  const headerActions = [
    {
      label: t("chemical.inventory.pdf"),
      icon: PrinterIcon,
      action: () =>
        run("pdf", async () => {
          try {
            await chemicalInventoryApi.exportPdf(id, `stoffkartotek-${id}.pdf`);
            toast.success(t("chemical.inventory.pdf_ready"));
          } catch (err) {
            toast.error(err.message || t("chemical.inventory.pdf_failed"));
          }
        }),
      hidden: loading || error,
      menu: false,
      ...busyProps("pdf"),
    },
    {
      label: t("chemical.inventory.new_revision"),
      icon: DocumentPlusIcon,
      action: handleNewRevision,
      hidden: !canWrite || creatingRevision,
      menu: false,
      disabled: creatingRevision || publishing,
      loading: creatingRevision,
    },
    {
      label: t("chemical.inventory.publish_review"),
      icon: CheckBadgeIcon,
      action: () => setShowPublishDialog(true),
      hidden: !canWrite,
      menu: false,
      disabled: publishing || creatingRevision,
    },
    {
      label: t("chemical.inventory.delete"),
      icon: TrashIcon,
      action: handleDelete,
      hidden: !canWrite,
      menu: true,
    },
  ];

  return (
    <>
      <HsePageChrome
        title={chemical.name}
        subtitle={t("chemical.inventory")}
        backTo="/dashboard/hse-ks/risk/chemical-inventory"
        actions={headerActions}
        mainButton={{
          label: t("chemical.inventory.edit"),
          icon: PencilIcon,
          action: () => setShowEditDialog(true),
          hidden: !canWrite,
        }}
      >
        <div className="mx-auto mt-8 max-w-7xl px-4 sm:px-6 lg:px-8">
          <RiskSubnav />

          <div className="bg-white shadow overflow-hidden sm:rounded-lg mb-6">
            <div className="px-4 py-5 sm:px-6">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  {getHmsLevelIcon(chemical.hms_level)}
                  <div>
                    <h3 className="text-lg leading-6 font-medium text-gray-900">
                      {chemical.name}
                    </h3>
                    <p className="mt-1 max-w-2xl text-sm text-gray-500">
                      {t("chemical.inventory.cas_number")}:{" "}
                      {chemical.cas_number || "—"}
                    </p>
                  </div>
                </div>
                <div className="flex items-center flex-wrap gap-2">
                  {getStatusBadge(chemical.status)}
                  {getHmsLevelBadge(chemical.hms_level)}
                  <ChemicalInventoryReviewBadge reviewState={chemical.review_state} />
                </div>
              </div>
            </div>
            <div className="border-t border-gray-200">
              <dl>
                <div className="bg-gray-50 px-4 py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                  <dt className="text-sm font-medium text-gray-500">
                    {t("chemical.inventory.chemical_formula")}
                  </dt>
                  <dd className="mt-1 text-sm text-gray-900 sm:col-span-2 sm:mt-0">
                    {chemical.chemical_formula || t("chemical.inventory.not_specified")}
                  </dd>
                </div>
                <div className="bg-white px-4 py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                  <dt className="text-sm font-medium text-gray-500">
                    {t("chemical.inventory.version")}
                  </dt>
                  <dd className="mt-1 text-sm text-gray-900 sm:col-span-2 sm:mt-0">
                    {chemical.version ?? "—"}
                  </dd>
                </div>
                <div className="bg-gray-50 px-4 py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                  <dt className="text-sm font-medium text-gray-500">
                    {t("chemical.inventory.last_review_date")}
                  </dt>
                  <dd className="mt-1 text-sm text-gray-900 sm:col-span-2 sm:mt-0">
                    {chemical.last_review_date
                      ? new Date(chemical.last_review_date).toLocaleDateString()
                      : t("chemical.inventory.not_specified")}
                  </dd>
                </div>
                <div className="bg-white px-4 py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                  <dt className="text-sm font-medium text-gray-500">
                    {t("chemical.inventory.next_review_date")}
                  </dt>
                  <dd className="mt-1 text-sm text-gray-900 sm:col-span-2 sm:mt-0">
                    {chemical.next_review_date
                      ? new Date(chemical.next_review_date).toLocaleDateString()
                      : t("chemical.inventory.not_specified")}
                  </dd>
                </div>
                <div className="bg-gray-50 px-4 py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                  <dt className="text-sm font-medium text-gray-500">
                    {t("chemical.inventory.reviewed_by")}
                  </dt>
                  <dd className="mt-1 text-sm text-gray-900 sm:col-span-2 sm:mt-0">
                    {chemical.reviewed_by?.displayTitle ||
                      chemical.reviewed_by?.name ||
                      t("chemical.inventory.not_specified")}
                  </dd>
                </div>
                <div className="bg-white px-4 py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                  <dt className="text-sm font-medium text-gray-500">
                    {t("chemical.inventory.hazard_symbols")}
                  </dt>
                  <dd className="mt-1 text-sm text-gray-900 sm:col-span-2 sm:mt-0">
                    <HazardSymbolsDisplay symbols={chemical.hazard_symbols} />
                  </dd>
                </div>
                <div className="bg-gray-50 px-4 py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                  <dt className="text-sm font-medium text-gray-500">
                    {t("chemical.inventory.first_aid")}
                  </dt>
                  <dd className="mt-1 text-sm text-gray-900 sm:col-span-2 sm:mt-0">
                    {chemical.first_aid || t("chemical.inventory.not_specified")}
                  </dd>
                </div>
              </dl>
            </div>
          </div>

          <div className="bg-white shadow overflow-hidden sm:rounded-lg mb-6">
            <div className="px-4 py-5 sm:px-6">
              <h3 className="text-lg leading-6 font-medium text-gray-900">
                {t("chemical.inventory.safety_info")}
              </h3>
            </div>
            <div className="border-t border-gray-200">
              <dl>
                <div className="bg-gray-50 px-4 py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                  <dt className="text-sm font-medium text-gray-500">
                    {t("chemical.inventory.hazard_statements")}
                  </dt>
                  <dd className="mt-1 text-sm text-gray-900 sm:col-span-2 sm:mt-0">
                    {chemical.hazard_statements || t("chemical.inventory.not_specified")}
                  </dd>
                </div>
                <div className="bg-white px-4 py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                  <dt className="text-sm font-medium text-gray-500">
                    {t("chemical.inventory.precautionary_statements")}
                  </dt>
                  <dd className="mt-1 text-sm text-gray-900 sm:col-span-2 sm:mt-0">
                    {chemical.precautionary_statements || t("chemical.inventory.not_specified")}
                  </dd>
                </div>
                <div className="bg-gray-50 px-4 py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                  <dt className="text-sm font-medium text-gray-500">
                    {t("chemical.inventory.signal_word")}
                  </dt>
                  <dd className="mt-1 text-sm text-gray-900 sm:col-span-2 sm:mt-0">
                    {chemical.signal_word || t("chemical.inventory.not_specified")}
                  </dd>
                </div>
              </dl>
            </div>
          </div>

          <div className="bg-white shadow overflow-hidden sm:rounded-lg mb-6">
            <div className="px-4 py-5 sm:px-6">
              <h3 className="text-lg leading-6 font-medium text-gray-900 flex items-center">
                <DocumentIcon className="h-5 w-5 mr-2" />
                {t("chemical.inventory.documents")}
              </h3>
            </div>
            <div className="border-t border-gray-200 px-4 py-5 sm:px-6">
              <DocumentManagerV6
                module="chemical_inventory"
                moduleParam={chemical.id}
                mode="dropzone"
                allowCopy={false}
                allowMove={false}
              />
            </div>
          </div>

          <div className="bg-white shadow overflow-hidden sm:rounded-lg mb-6">
            <div className="px-4 py-5 sm:px-6">
              <h3 className="text-lg leading-6 font-medium text-gray-900 flex items-center">
                <ChatBubbleLeftRightIcon className="h-5 w-5 mr-2" />
                {t("chemical.inventory.notes")}
              </h3>
            </div>
            <div className="border-t border-gray-200 px-4 py-5 sm:px-6">
              <NotesDeluxeSection
                title={t("chemical.inventory.notes")}
                notes={chemical.chemical_inventory_notes || []}
                onNoteSubmit={() => {}}
              />
            </div>
          </div>

          <div id="chemical-revisions" className="bg-white shadow overflow-hidden sm:rounded-lg mb-6">
            <div className="px-4 py-5 sm:px-6">
              <h3 className="text-lg leading-6 font-medium text-gray-900 flex items-center">
                <HistoryIcon className="h-5 w-5 mr-2" />
                {t("chemical.inventory.history")}
              </h3>
            </div>
            <div className="border-t border-gray-200">
              {historyItems.length === 0 ? (
                <p className="px-4 py-5 text-sm text-gray-500">
                  {t("chemical.inventory.history_empty")}
                </p>
              ) : (
                <ul className="divide-y divide-gray-200">
                  {historyItems.map((history, index) => (
                    <li key={history.id || index} className="px-4 py-4">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center space-x-3">
                          <div className="flex-shrink-0">
                            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                              {history.action ||
                                history.revision_status ||
                                history.change_summary ||
                                t("chemical.inventory.revision")}
                            </span>
                          </div>
                          <div>
                            <p className="text-sm font-medium text-gray-900">
                              {history.user_name ||
                                history.reviewed_by?.displayTitle ||
                                history.user?.displayTitle ||
                                "—"}
                            </p>
                            <p className="text-sm text-gray-500">
                              {history.created_at
                                ? new Date(history.created_at).toLocaleString()
                                : history.published_at
                                  ? new Date(history.published_at).toLocaleString()
                                  : ""}
                            </p>
                            {history.change_summary ? (
                              <p className="mt-1 text-sm text-gray-600">
                                {history.change_summary}
                              </p>
                            ) : null}
                          </div>
                        </div>
                        <div className="text-sm text-gray-500">
                          {t("chemical.inventory.version")}{" "}
                          {history.version ?? history.revision_number ?? "—"}
                        </div>
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>
        </div>
      </HsePageChrome>

      <ChemicalInventoryForm
        open={showEditDialog}
        onClose={() => setShowEditDialog(false)}
        chemicalInventory={chemical}
        onSuccess={() => {
          fetchChemical();
          setShowEditDialog(false);
        }}
      />

      <PMDialog
        open={showPublishDialog}
        onClose={() => setShowPublishDialog(false)}
        title={t("chemical.inventory.publish_review")}
      >
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700">
              {t("chemical.inventory.change_summary")} *
            </label>
            <textarea
              value={changeSummary}
              onChange={(e) => setChangeSummary(e.target.value)}
              rows={4}
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700">
              {t("chemical.inventory.next_review_date")}
            </label>
            <input
              type="date"
              value={nextReviewDate}
              onChange={(e) => setNextReviewDate(e.target.value)}
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
            />
          </div>
          <div className="flex justify-end gap-2">
            <button
              type="button"
              onClick={() => setShowPublishDialog(false)}
              className="rounded-md border border-gray-300 bg-white px-3 py-2 text-sm font-medium text-gray-700"
            >
              {t("Avbryt")}
            </button>
            <button
              type="button"
              onClick={handlePublishReview}
              disabled={publishing}
              className="rounded-md bg-indigo-600 px-3 py-2 text-sm font-medium text-white disabled:opacity-50"
            >
              {t("chemical.inventory.publish_review")}
            </button>
          </div>
        </div>
      </PMDialog>
    </>
  );
};
